/**
 * Experiences (`src/features/experiences/**`): list, search sheet, detail, checkout (pickup, free entry,
 * provider details), success page and voucher. French copied verbatim from the code it replaces.
 * Server content (titles, descriptions, option and add-on titles, provider questions) is never here.
 */
export const fr = {
  /* Head tags (seo.ts) */
  'seo.morocco': 'Maroc',
  'seo.list.title': 'Expériences et excursions à {where}',
  'seo.list.titleCategory': '{category} à {where} · Expériences',
  'seo.list.description':
    'Excursions, activités, visites et transferts à {where} avec des prestataires vérifiés : prix affiché, paiement sécurisé en ligne et bon envoyé par e-mail.',
  'seo.crumb.home': 'Accueil',
  'seo.notFound': 'Expérience introuvable',
  'seo.checkout': 'Réservation de l’expérience',
  'seo.booked': 'Expérience réservée',

  /* Shared states */
  'gone.title': 'Cette expérience n’est plus disponible',
  'gone.text': 'Réessayez dans un instant.',
  seeExperiences: 'Voir les expériences',

  /* Hero (list) */
  'hero.imageAlt': 'Campement dans le désert d’Agafay',
  'hero.tag': 'Nouveau sur Cardnd',
  'hero.lead': 'Le Maroc,',
  'hero.accent': 'vécu de près.',
  'hero.text':
    'Désert, montagnes, médinas : des excursions guidées et des transferts réservés en ligne, au prix affiché. Chaque sortie est encadrée par un prestataire vérifié.',
  'hero.securePayment': 'Paiement en ligne sécurisé',
  'hero.voucher': 'Bon envoyé par e-mail',
  'hero.pickup': 'Prise en charge à {city}',
  'hero.trust.providers': 'Prestataires vérifiés',
  'hero.trust.realtime': 'Disponibilités en temps réel',
  'hero.trust.cars': 'Voitures à louer sur place',

  /* Hero card (ExperienceShowcase) */
  'showcase.live': 'À vivre · {city}',
  'showcase.open': 'Voir les expériences à {city}',
  'showcase.meta': '{duration} · {group}',
  'showcase.note': '<b>Carte pré-autorisée</b>, débitée seulement quand le prestataire confirme',

  /* Search (hero bar and sheet) */
  'search.where': 'Où ?',
  'search.when': 'Quand ?',
  'search.category': 'Catégorie',
  'search.duration': 'Durée',
  'search.addDate': 'Ajouter une date',
  'search.allCategories': 'Toutes les catégories',
  'search.flexible': 'Dates flexibles',
  'search.submit': 'Rechercher',
  'search.title': 'Où et quand ?',
  'search.destination': 'Rechercher une destination',
  'search.destinationLabel': 'Destination',
  'search.suggestions': 'Suggestions',
  'search.clear': 'Effacer',
  'search.show': { one: 'Afficher {count} expérience', other: 'Afficher {count} expériences' },
  /** The «all durations» chip (feminine, like «Toutes» for the categories). */
  'search.allDurations': 'Toutes',
  'duration.court': '< 1h',
  'duration.moyen': '1 – 3h',
  'duration.long': '3 – 6h',
  'duration.journee': '+ 6h',

  /* List */
  /* `<noun>` only keeps the text nodes of the JSX it replaced, so the French eyebrow does not move by a sub-pixel. */
  'list.count': {
    one: '{count} <noun>expérience</noun> au départ de {city}',
    other: '{count} <noun>expériences</noun> au départ de {city}',
  },
  'list.title': 'Choisissez votre <serif>aventure.</serif>',
  'list.searchFilter': 'Rechercher et filtrer',
  'list.clearSearch': 'Effacer la recherche',
  'list.error.title': 'Impossible de charger les expériences',
  'list.error.text': 'Vérifiez votre connexion, puis réessayez.',
  'list.empty.title': 'Aucune expérience ne correspond',
  'list.empty.text': 'Essayez une autre durée ou une autre catégorie.',
  'list.empty.clear': 'Effacer les filtres',
  'list.categories': 'Catégories',

  /* Cars cross-link */
  'cars.eyebrow': 'Expérience + voiture',
  'cars.title': 'Continuez le voyage en voiture',
  'cars.text': 'Louez une voiture auprès d’agences vérifiées à {city}, au prix affiché, et partez à votre rythme.',
  'cars.cta': 'Voir les voitures',

  /* Date strip and departure times */
  'days.loading': 'Chargement des dates',
  'days.label': 'Date',
  'day.full': 'Complet',
  'day.low': 'Peu de pl.',
  'day.open': 'Dispo',
  'day.fullLabel': '{day}, complet',
  'day.lowLabel': '{day}, dernières places',
  'day.openLabel': '{day}, disponible',
  'times.label': 'Heure de départ',

  /* Detail */
  'detail.shellTitle': 'Expérience',
  'detail.crumb': 'Fil d’Ariane',
  'detail.departure': 'Départ {time}',
  'detail.from': 'À partir de',
  'detail.date': 'Date',
  'detail.slotsError': 'Les départs n’ont pas pu être chargés.',
  'detail.full': 'Complet sur ces dates : choisissez une autre date dans la recherche.',
  'detail.participants': 'Participants',
  'detail.passengers': 'Passagers',
  'detail.fixedPerVehicle': 'Prix fixe par véhicule · max. {max}',
  'detail.perPerson': '{price} / pers.',
  'detail.perPersonAt': '{price} / pers. à {count}',
  'detail.perChild': '{price} / enfant',
  'detail.childFree': 'Gratuit',
  'detail.infantsHint': 'Moins de 2 ans · sans place',
  'detail.book': 'Réserver',
  'detail.bookTotal': 'Réserver · {total}',
  'detail.payNote': 'Paiement intégral en ligne · bon envoyé par e-mail',
  'detail.packs': 'Choisissez votre formule',
  'detail.packsLabel': 'Formule',
  'detail.pack.pickup': 'Prise en charge à l’hôtel',
  'detail.pack.freeEntry': 'Gratuité sur justificatif',
  'detail.pack.popular': 'Le plus populaire',
  'detail.addons': 'Options',
  'detail.programme': 'Au programme',
  'detail.practical': 'Informations pratiques',
  'detail.included': 'Inclus',
  'detail.excluded': 'Non inclus',
  'detail.bring': 'À prévoir',
  'detail.meetingPoint': 'Point de rencontre',
  'detail.reviews': 'Avis',
  'detail.noReviews': 'Pas encore d’avis',
  'detail.noReviewsText': 'Les voyageurs notent l’expérience après sa date.',
  'detail.chooseDate': 'Choisissez une date',
  'detail.loading': 'Chargement de l’expérience',
  /* AC-02: a privatisation option's flat price; API-ACT-1: the clients' rating */
  'detail.perGroup': '/ groupe',
  'detail.rating': 'Note des voyageurs',
  'detail.ratingCount': { one: '{count} avis', other: '{count} avis' },
  /* Cross-sell rail (API-ACT-16) */
  'detail.more.title': 'Autres expériences à <serif>{city}.</serif>',
  'detail.more.text': 'Découvrez d’autres expériences proposées dans la même ville.',
  'detail.more.all': 'Toutes les expériences',

  /* Group sizes and parties (selection.ts) */
  people: { one: '{count} personne', other: '{count} personnes' },
  'bounds.upTo': { one: 'Jusqu’à {count} personne', other: 'Jusqu’à {count} personnes' },
  'bounds.range': { one: 'De {min} à {count} personne', other: 'De {min} à {count} personnes' },
  'party.adults': { one: '{count} adulte', other: '{count} adultes' },
  'party.passengers': { one: '{count} passager', other: '{count} passagers' },
  /** Headcount in the sticky bars («3 pers.»). */
  'party.short': { one: '{count} pers.', other: '{count} pers.' },

  /* Checkout */
  'checkout.incomplete': 'Sélection incomplète',
  'checkout.chooseDate': 'Choisir une date',
  'checkout.incompleteText': 'Choisissez une date et vos participants sur la fiche de l’expérience.',
  'checkout.saved': 'Réservation enregistrée',
  'checkout.steps': 'Étapes de la réservation',
  'checkout.step.experience': 'Expérience',
  'checkout.step.booking': 'Réservation & paiement',
  'checkout.step.confirmation': 'Confirmation',
  'checkout.title': 'Plus que quelques <serif>détails.</serif>',
  'checkout.lead': 'Paiement sécurisé en ligne, bon envoyé par e-mail dès la confirmation.',
  'checkout.experience': 'Votre expérience',
  'checkout.recap.date': 'DATE',
  'checkout.recap.pack': 'FORMULE',
  'checkout.recap.party': 'PARTICIPANTS',
  'checkout.recap.addons': 'OPTIONS',
  'checkout.recap.none': 'Aucune',
  'checkout.dayGone': '<b>Ce départ n’est plus disponible.</b> Choisissez une autre date sur la fiche de l’expérience.',
  'checkout.contact': 'Vos coordonnées',
  'checkout.field.firstName': 'Prénom *',
  'checkout.field.lastName': 'Nom *',
  'checkout.field.email': 'E-mail *',
  'checkout.field.phone': 'Téléphone',
  'checkout.voucherTo': 'Votre bon de réservation est envoyé à cette adresse.',
  'checkout.account.title': 'Un compte est nécessaire',
  'checkout.account.text':
    'Connectez-vous ou créez un compte : votre bon et vos échanges avec le prestataire y sont rattachés.',
  'checkout.terms':
    'J’ai lu et j’accepte les <terms>Conditions générales</terms> et la <privacy>Politique de confidentialité</privacy>.',
  'checkout.payment': 'Paiement',
  'checkout.payFull': 'Vous payez la totalité en ligne',
  'checkout.payFullText':
    'Le total est pré-autorisé sur votre carte à la réservation, puis débité quand le prestataire confirme ; votre bon est alors envoyé par e-mail. Rien à régler sur place.',
  'checkout.setByCardnd': 'Défini par Cardnd',
  'checkout.today': 'Aujourd’hui, pré-autorisé',
  'checkout.card': 'Carte bancaire',
  'checkout.stripe.title': 'Formulaire de carte sécurisé Stripe',
  'checkout.stripe.text': 'Il s’ouvre après « Payer », avec le montant exact pré-autorisé sur votre carte.',
  'checkout.fx':
    'Votre carte est débitée en {currency}, au taux de Cardnd au moment du paiement : le montant exact s’affiche avant la validation de la carte. Les montants en {currency} de cette page sont indicatifs.',
  'checkout.pay': 'Payer',
  'checkout.payTotal': 'Payer {total}',
  'checkout.summary': 'Récapitulatif',
  'checkout.policy': 'Si vos plans changent : <b>{cancellation}</b>',

  /* Why «Payer» is not available yet (checkout.ts `PayBlocker`) */
  'blocker.day': 'Ce départ n’est plus disponible : choisissez une autre date sur la fiche de l’expérience.',
  'blocker.eligibility': 'Le paiement s’ouvre quand le prestataire a accepté votre demande de gratuité.',
  'blocker.pickup': 'Choisissez votre point de prise en charge.',
  'blocker.cutoff': 'Les réservations pour ce départ sont closes : choisissez un autre départ.',
  'blocker.quote': 'Calcul du prix en cours…',

  /* Checkout form errors (checkout.ts) */
  'error.firstName': 'Indiquez votre prénom.',
  'error.lastName': 'Indiquez votre nom.',
  'error.email': 'Indiquez une adresse e-mail valide.',
  'error.terms': 'Acceptez les conditions générales pour continuer.',
  'error.answerRequired': 'Réponse obligatoire.',
  'error.answerChoice': 'Choisissez une des réponses proposées.',
  'error.answerTime': 'Indiquez une heure au format HH:MM.',
  'error.answerLength': '1 000 caractères au maximum.',
  'error.waiver': 'Acceptez la décharge du prestataire.',
  'error.age': 'Âge en années (0 à 120).',
  /* `zero` keeps the former «0 ans»; 1 now reads «1 an». */
  'error.minAge': {
    zero: 'Âge minimum : {count} ans.',
    one: 'Âge minimum : {count} an.',
    other: 'Âge minimum : {count} ans.',
  },
  'error.driverMinAge': {
    zero: 'Conducteur : {count} ans minimum.',
    one: 'Conducteur : {count} an minimum.',
    other: 'Conducteur : {count} ans minimum.',
  },
  'error.passengerMinAge': {
    zero: 'Passager : {count} ans minimum.',
    one: 'Passager : {count} an minimum.',
    other: 'Passager : {count} ans minimum.',
  },
  'error.adultCompanion': 'Un accompagnateur adulte (18 ans ou plus) est obligatoire.',
  'error.height': 'Taille en centimètres.',
  'error.minHeight': 'Taille minimum : {cm} cm.',
  'error.weight': 'Poids en kilogrammes.',

  /* Provider details (ProviderDetails, checkout.ts) */
  'details.questions': 'Questions du prestataire',
  'details.choose': 'Choisir…',
  'details.minAge': {
    zero: 'Âge minimum {count} ans.',
    one: 'Âge minimum {count} an.',
    other: 'Âge minimum {count} ans.',
  },
  'details.minHeight': 'Taille minimum {cm} cm.',
  'details.adultCompanion': 'Un adulte de 18 ans ou plus accompagne le groupe.',
  'details.default': 'Le prestataire a besoin de ces informations pour préparer votre venue.',
  'details.drivers': 'Conducteurs',
  'details.perVehicle': { one: '{count} pers. par véhicule', other: '{count} pers. par véhicule' },
  'details.driverMinAge': { zero: '{count} ans minimum', one: '{count} an minimum', other: '{count} ans minimum' },
  'details.driver': 'conducteur',
  'details.passenger': 'passager',
  'details.age': 'Âge *',
  'details.years': 'ans',
  'details.height': 'Taille *',
  'details.weight': 'Poids *',
  'details.weightReview': 'Au-delà de {kg} kg, le prestataire confirme lui-même la participation avant le débit.',
  'details.waiver': 'J’accepte la décharge de responsabilité du prestataire.',
  'details.waiverDoc': 'J’accepte la décharge de responsabilité du prestataire (<doc>lire le document</doc>).',
  'participant.adult': 'Participant {n} · adulte',
  'participant.child': 'Participant {n} · enfant',
  'participant.infant': 'Participant {n} · bébé',

  /* Free entry (FreeEntryBlock) */
  'free.title': 'Demander la gratuité',
  'free.signIn': '<link>Connectez-vous</link> pour envoyer votre demande au prestataire.',
  'free.intro':
    'Combien de participants entrent gratuitement ? Le prestataire vérifie le justificatif avant le paiement.',
  'free.adults': 'Adultes gratuits',
  'free.children': 'Enfants gratuits',
  'free.infants': 'Bébés gratuits',
  'free.none': 'Indiquez au moins un participant gratuit.',
  'free.proof': 'Référence du justificatif transmis au prestataire *',
  'free.proofRequired': 'Indiquez la référence du justificatif.',
  'free.send': 'Envoyer la demande',
  'free.reading': 'Lecture de votre demande…',
  'free.pending': '<b>Demande envoyée, en attente du prestataire.</b> Le paiement s’ouvre dès qu’il l’accepte.',
  'free.approved': '<b>Gratuité accordée.</b> Le prix ci-contre en tient compte.',
  'free.used': 'Cette gratuité a déjà servi pour une réservation : faites une nouvelle demande.',
  'free.rejected': '<b>Demande refusée.</b> {note}',
  'free.rejectedDefault': 'Le justificatif n’a pas été accepté.',
  'free.refresh': 'Actualiser',
  'free.restart': 'Nouvelle demande',

  /* Pickup (PickupPicker) */
  'pickup.title': 'Prise en charge à votre hôtel ou riad',
  'pickup.places': 'Point de prise en charge',
  'pickup.included': 'Inclus',
  'pickup.address': 'Adresse de votre hôtel ou riad',
  'pickup.addressPlaceholder': 'Nom de l’hôtel, rue, quartier',
  'pickup.addressRequired': 'Indiquez l’adresse de prise en charge.',
  'pickup.addressHint': 'Le prestataire confirme la prise en charge à cette adresse.',
  'pickup.useDevice': 'Utiliser ma position',
  'pickup.link': 'Ou un lien Google Maps de l’hôtel',
  'pickup.locate': 'Partagez la position de votre hôtel pour vérifier la zone de prise en charge.',
  'pickup.error.noGeo': 'Votre appareil ne partage pas sa position : collez plutôt un lien Google Maps.',
  'pickup.error.denied': 'Position indisponible : autorisez la localisation ou collez un lien Google Maps.',
  'pickup.error.badLink': 'Ce lien ne contient pas de position : ouvrez le lieu dans Google Maps, puis copiez le lien.',
  'pickup.verdict.place': '{place}.',
  'pickup.verdict.placeFee': '{place} · supplément {fee}.',
  'pickup.verdict.served': 'Adresse desservie',
  'pickup.verdict.confirm': 'Le prestataire confirmera la prise en charge à cette adresse.',
  'pickup.verdict.outside': 'Cette adresse n’est pas desservie par la prise en charge : choisissez une autre adresse.',

  /* Success page */
  'booked.shellTitle': 'Réservation confirmée',
  'booked.notFound': 'Réservation introuvable',
  'booked.notFoundText': 'Retrouvez toutes vos réservations dans Mes voyages.',
  'booked.paid': 'Paiement effectué',
  'booked.confirming': 'Confirmation en cours',
  'booked.title': 'C’est réservé.',
  'booked.titleNamed': 'C’est réservé, <serif>{name}.</serif>',
  'booked.confirmed': 'Votre bon a été envoyé par e-mail. Présentez-le à votre guide le jour J.',
  'booked.confirmedTo': 'Votre bon a été envoyé à {email}. Présentez-le à votre guide le jour J.',
  'booked.pending':
    'Votre carte est pré-autorisée. Le prestataire confirme votre réservation : le débit a lieu à ce moment et votre bon vous est envoyé par e-mail.',
  'booked.pendingTo':
    'Votre carte est pré-autorisée. Le prestataire confirme votre réservation : le débit a lieu à ce moment et votre bon vous est envoyé à {email}.',
  'booked.cross': '<b>Besoin d’une voiture sur place ?</b> Louez auprès d’agences vérifiées à {city}, au prix affiché.',
  'booked.rentCar': 'Louer une voiture',
  'booked.trips': 'Mes voyages',
  'booked.more': 'Autres expériences',
  'booked.calendar': 'Ajouter au calendrier',
  'booked.help': 'Besoin d’aide ?',
  'booked.downloading': 'Téléchargement…',
  'booked.download': 'Télécharger le bon (PDF)',
  'booked.tickets.ready': 'Vos billets d’entrée sont prêts.',
  /** Followed by the tickets' links (trailing space kept). */
  'booked.tickets.time': 'Heure confirmée : {time}. ',
  'booked.tickets.resent': 'Billets renvoyés par e-mail',
  'booked.tickets.resend': 'Renvoyer les billets par e-mail',
  'booked.loading': 'Chargement de la réservation',

  /* Voucher */
  'voucher.label': 'Bon d’expérience {ref}',
  'voucher.kicker': 'BON D’EXPÉRIENCE',
  'voucher.confirmed': 'Confirmé',
  'voucher.pending': 'En attente',
  'voucher.date': 'Date',
  'voucher.pin': 'Code PIN',
  'voucher.qr': 'QR code de la réservation {ref}',
  'voucher.ref': 'N° de réservation',
  'voucher.paid': 'Total payé',
  'voucher.held': 'Total pré-autorisé',
} as const;
